import { Button } from "@aurumiq/ui/components/button";
import { Input } from "@aurumiq/ui/components/input";
import {
  type ColumnDef,
  createColumnHelper,
  DataTable,
  type PaginationState,
} from "@aurumiq/ui/composite/data-table";
import { StatusTag, statusVariant } from "@aurumiq/ui/composite/status-tag";
import { dmy } from "@aurumiq/ui/lib/format";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { PageHeader } from "@/app/page-header";
import { usePermission } from "@/auth/use-permission";
import { type OrgSummary, type Paginated, rawApi } from "@/lib/api";

const col = createColumnHelper<OrgSummary>();

const columns = [
  col.accessor("code", {
    header: "Code",
    cell: (c) => <span className="font-mono">{c.getValue()}</span>,
  }),
  col.accessor("name", {
    header: "Organisation",
    cell: (c) => (
      <Link
        to={`/orgs/${c.row.original.id}`}
        className="font-semibold text-primary hover:underline"
      >
        {c.getValue()}
      </Link>
    ),
  }),
  col.accessor("status", {
    header: "Status",
    cell: (c) => (
      <StatusTag variant={statusVariant(c.getValue())}>{c.getValue().toUpperCase()}</StatusTag>
    ),
  }),
  col.accessor("plan_code", { header: "Plan" }),
  col.accessor((r) => `${r.seats_used}/${r.seats}`, {
    id: "seats",
    header: "Seats",
    meta: { numeric: true },
  }),
  col.accessor("created_at", {
    header: "Created",
    cell: (c) => dmy(c.getValue()),
    meta: { numeric: true },
  }),
];

export function OrgsPage() {
  const navigate = useNavigate();
  const canCreate = usePermission("orgs:write");
  const [q, setQ] = useState("");
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 25 });

  const orgs = useQuery({
    queryKey: ["admin", "orgs", { q, ...pagination }],
    placeholderData: keepPreviousData,
    queryFn: () =>
      rawApi.get<Paginated<OrgSummary>>(
        `/api/v1/admin/orgs?page=${pagination.pageIndex + 1}&page_size=${pagination.pageSize}&q=${encodeURIComponent(q)}`,
      ),
  });

  const total = orgs.data?.total ?? 0;

  return (
    <>
      <PageHeader
        title="Organisations"
        description="Every customer tenant, its plan and seat usage."
        actions={
          canCreate ? (
            <Button asChild variant="gold">
              <Link to="/orgs/new">
                <Plus /> New organisation
              </Link>
            </Button>
          ) : null
        }
      />
      <DataTable
        columns={columns as ColumnDef<OrgSummary, unknown>[]}
        data={orgs.data?.items ?? []}
        isLoading={orgs.isPending}
        pageCount={Math.max(1, Math.ceil(total / pagination.pageSize))}
        pagination={pagination}
        onPaginationChange={setPagination}
        onRowClick={(row) => navigate(`/orgs/${row.original.id}`)}
        emptyMessage={
          orgs.isError
            ? "Could not load organisations (API unavailable or endpoint not implemented yet)."
            : "No organisations yet."
        }
        toolbar={
          <Input
            placeholder="Search name or code…"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPagination((p) => ({ ...p, pageIndex: 0 }));
            }}
            className="max-w-xs"
          />
        }
      />
    </>
  );
}
